import {useCallback, useEffect, useState, useSyncExternalStore} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {descartarAviso, leerAvisos, suscribirAvisos, type Aviso} from '@/lib/avisos'
import {cn} from '@/lib/utils'

/** Solo para éxitos: un error nunca se cierra solo. */
const MS_AUTOCIERRE = 6000
/** Duración de `.burbuja[data-saliendo]` en `index.css`: la burbuja se desmonta después de la salida. */
const MS_SALIDA = 160

export function AvisosRegion() {
  const {t} = useTranslation()
  const avisos = useSyncExternalStore(suscribirAvisos, leerAvisos)
  const [pausado, setPausado] = useState(false)
  const [salientes, setSalientes] = useState<readonly number[]>([])

  // Estable (solo usa el setter): el efecto de auto-cierre depende de ella sin re-armarse.
  const cerrar = useCallback((id: number): void => {
    setSalientes((previos) => (previos.includes(id) ? previos : [...previos, id]))
    setTimeout(() => {
      setSalientes((previos) => previos.filter((otro) => otro !== id))
      descartarAviso(id)
    }, MS_SALIDA)
  }, [])

  // Al soltar el hover el conteo reinicia en vez de reanudar: más generoso y sin llevar milisegundos.
  useEffect(() => {
    if (pausado) return
    const efimeros = avisos.filter((aviso) => aviso.tipo === 'exito')
    if (efimeros.length === 0) return
    const relojes = efimeros.map((aviso) => setTimeout(() => cerrar(aviso.id), MS_AUTOCIERRE))
    return () => relojes.forEach(clearTimeout)
  }, [avisos, pausado, cerrar])

  if (avisos.length === 0) return null

  return (
    <div
      data-testid="avisos-region"
      // `polite`: el error interrumpe por su propio `role="alert"`, no la región entera.
      aria-live="polite"
      className={cn(
        'fixed inset-x-3 bottom-3 z-50 flex flex-col gap-2',
        'pb-[env(safe-area-inset-bottom)]',
        'sm:inset-x-auto sm:right-4 sm:bottom-4 sm:w-[min(24rem,calc(100vw-2rem))]'
      )}
      onMouseEnter={() => setPausado(true)}
      onMouseLeave={() => setPausado(false)}>
      {avisos.map((aviso) => (
        <Burbuja
          key={aviso.id}
          aviso={aviso}
          saliendo={salientes.includes(aviso.id)}
          cerrarLabel={t('avisos.cerrar')}
          onCerrar={() => cerrar(aviso.id)}
        />
      ))}
    </div>
  )
}

function Burbuja({
  aviso,
  saliendo,
  cerrarLabel,
  onCerrar,
}: {
  aviso: Aviso
  saliendo: boolean
  cerrarLabel: string
  onCerrar: () => void
}) {
  const esError = aviso.tipo === 'error'
  return (
    <div
      data-testid={aviso.testId ?? `burbuja-${aviso.id}`}
      data-aviso-tipo={aviso.tipo}
      data-saliendo={saliendo ? 'true' : undefined}
      role={esError ? 'alert' : 'status'}
      className="burbuja surface flex gap-3 overflow-hidden p-4 shadow-[var(--shadow-pop)]">
      <span
        aria-hidden="true"
        className={cn('mt-1.5 size-2 shrink-0 rounded-full', esError ? 'bg-tone-red-fg' : 'bg-primary')}
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{aviso.titulo}</p>
        <p className="mt-1 text-sm text-muted-foreground">{aviso.detalle}</p>
        {aviso.accion && (
          <Button size="sm" variant="outline" className="mt-2.5" onClick={aviso.accion.alActivar}>
            {aviso.accion.etiqueta}
          </Button>
        )}
      </div>
      <button
        type="button"
        aria-label={cerrarLabel}
        onClick={onCerrar}
        className="h-5 shrink-0 cursor-pointer font-mono text-sm leading-none text-muted-foreground hover:text-foreground">
        ×
      </button>
    </div>
  )
}
